import { FileModel, SecurityDepositTableQuery } from 'src/app/shared/shared.model';
import {
  ApplicationDeclineTypeCode,
  ApplicationFile,
  IncomeLevelCode,
  IncomeSourceTypeCode,
  ProgressStatusCode
} from '../../applications/models/applications.model';
import {
  ProductTypeCode,
  ApplicationStatusCode
} from '../../common/models/application-general.model';
import { ClaimStatusCode } from '../../claims/models/claims.model';

export enum ResidentGroup {
  activeLease = 1,
  postLease = 2,
  claims = 3,
  legacy = 4,
  optOut = 5
}

export class ResidentReference {
  id?: string;
  userId?: string;
  applicationId?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  phone?: string;
  corporateId?: string;
  propertyManagerId?: string;
  propertyManagerName?: string;
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  managerUserId?: string;
  apartment?: string;
  rentAmount?: number;
  depositAmount?: number;
  leaseStartDate?: Date;
  leaseEndDate?: Date;
  programLeaseEndDate?: Date;
  isRenewalOptIn?: boolean;
  leaseTerm?: number;
}

export class Resident {
  id?: string;
  userId?: string;
  propertyManagerId?: string;
  propertyManagerName?: string;
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  managerUserId?: string;
  apartment?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  leaseEndDate?: Date;
  leaseStartDate?: Date;
  programLeaseEndDate?: Date;
  isRenewalOptIn?: boolean;
  leaseTerm?: number;
  phone?: string;
  depositAmount?: number;
  rentAmount?: number;
  statusCode?: ResidentStatusCode;
  createTimeUtc?: Date;
  updateTimeUtc?: Date;
  applicationId?: string;
  productTypeCode?: ProductTypeCode;
  declineTypeCode?: ApplicationDeclineTypeCode
  isStanding?: boolean;
  IsRenewalEligible?: boolean;
  propertyId?: string;
  lastUpdateNote?: string;
  status?: string;
  securityDeposit?: number;
  monthlyRent?: number;
  coverageAmount?: number;
  claimId?: number;
  claimStatusCode?: ClaimStatusCode;
  claimCreatedDate?: Date;
  claimUpdatedDate?: Date;
  claimAmount?: number;
  isClaimPaid?: boolean;
  isClaimManagementAvailable?: boolean;
  isClaimOpeningAvailable?: boolean;
  isClaimEditingAvailable?: boolean;
  deploymentAmount?: number;
  isEditEndOfLeaseAvailable?: boolean;
  isRefundAvailable?: boolean;
  isFsdNotManaged?: boolean;
  claim?: Claim;
  externalId?: string;
}

export class ResidentsQuery extends SecurityDepositTableQuery<ResidentsQueryParameter> {
  statusCodes?: ResidentStatusCode[];
  isHasClaim?: boolean;
  isShowOnlyChargebacks?: boolean;
  residentGroup?: ResidentGroup;
  property?: string;
  apartment?: string;
  tenantName?: string;
  search?: string;
  isRenewalOptIn?: boolean;
  leaseStartDate?: Date;
  leaseEndDate?: Date;
}

export enum ResidentStatusCode {
  invite = 1,
  application = 2,
  approved = 3,
  funded = 4,
  leaseEnded = 5,
  evicted = 6,
  pendingRefund = 7,
  refunded = 8,
  archived = 9,
  chargebacked = 10,
  claimPaidOut = 11,
  leaseStarted = 12,
  claimOpened = 13,
  claimStatusChanged = 14,
  leaseStartDateChanged = 15,
  leaseEndDateChanged = 16,
  renewalOptOut = 17,
  renewalOptIn = 18,
  interestPaid = 19
}

export enum ResidentsQueryParameter {
  сreateTimeUtc = 1,
  updateTimeUtc = 2,
  name = 3,
  email = 4,
  phone = 5,
  rentAmount = 6,
  depositAmount = 7,
  leaseStartDate = 8,
  leaseEndDate = 9,
  leaseTerm = 10
  /*
    property = 1,
    apartment = 2,
    tenantName = 3,
    leaseStart = 4,
    leaseEnd = 5,
    claimUpdate = 6,
    updateDate = 7*/
}

export class RequestResidentDetails {
  applicationId?: string;
  residentId?: string;
}

export class ResidentDetails {
  id?: string;
  externalId?: string;
  applicationId?: string;
  userId?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  phone?: string;
  corporateId?: string;
  propertyManagerId?: string;
  propertyManagerName?: string;
  managerUserId?: string;
  apartment?: string;
  rentAmount?: number;
  depositAmount?: number;
  coverageAmount?: number;
  deployedAmount?: number;
  statusCode?: ResidentStatusCode;
  leaseStartDate?: Date;
  leaseEndDate?: Date;
  programLeaseEndDate?: Date;
  isRenewalOptIn?: boolean;
  leaseTerm?: number;
  declineTypeCode?: ApplicationDeclineTypeCode;
  incomeLevelCode?: IncomeLevelCode;
  incomeSourceTypeCode?: IncomeSourceTypeCode;
  productTypeCode?: ProductTypeCode;
  isEditApplicationDataAvailable?: boolean;
  uploadedDocuments!: ApplicationFile[];
  createTimeUtc?: Date;
  updateTimeUtc?: Date;
  address!: string;
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  yearlySalary?: number;
  screeningFile!: ApplicationFile;
  claim?: Claim;
  history?: ResidentDetailsHistory[];
  interestPayments?: InterestPayment[];
  isClaimManagementAvailable?: boolean;
  isCanUpdateResident?: boolean;
  isFsdNotManaged?: boolean;
  isRefundAvailable?: boolean;
  isEditEndOfLeaseAvailable?: boolean;
  refundedByPaperCheck?: boolean;
}

export class ResidentDetailsHistory {
  statusCode?: ResidentStatusCode;
  depositAmount?: number;
  leaseStartDate?: Date;
  leaseEndDate?: Date;
  comment?: string;
  isRenewalOptIn?: boolean;
  interestAmount?: string;
  createTimeUtc?: Date;
}

export class InterestPayment {
  amount?: string;
  createTimeUtc?: Date;
}

export class Claim {
  id?: string;
  updateDate?: Date;
  createDate?: Date;
  amount?: string;
  isPaid?: boolean;
  description?: string;
  statusCode?: ClaimStatusCode;
}

export class ClaimDetails {
  id!: number;
  createDate!: Date;
  updateDate!: Date;
  amount?: number;
  isPaid?: boolean;
  statusCode?: ClaimStatusCode;
  description?: string;
  isClaimEditingAvailable?: boolean;
  hasResidentAcknowledged?: boolean;
  documents?: ClaimFile[]
  history?: ClaimHistory[]
  messages?: ClaimMessage[]
}

export class ClaimMessage {
  text?: string;
  sentAt?: Date;
  senderName?: string;
}

export class SendClaimMessage {
  message!: string;
}

export class ClaimFile {
  fileTypeCode!: FileTypeCode;
  name?: string;
  url?: string;
}

export enum FileTypeCode {
  proof = 1,
  contract = 2,
  leaseAgreement = 3,
  dispositionLetter = 4,
  ledger = 5,
  screeningReport = 6
}

export class ClaimHistory {
  amount?: number;
  statusCode?: ClaimStatusCode;
  comment?: string;
  createTimeUtc?: Date;
}

export class RequestClaimDetails {
  tenantId?: string;
  applicationId?: string;
  claimId?: number;
}

export class CreateTenantClaim {
  residentId!: string;
  propertyManagerId!: string;
  claimAmount!: string;
  claimReason?: string;
  signature?: FileModel;
  files?: FileModel[];
}

export class UpdateTenantClaim {
  residentId!: string;
  propertyManagerId!: string;
  claimAmount!: string;
  claimReason?: string;
  signature?: FileModel;
  existingFileCount?: number;
  files?: FileModel[];
}

export class UpdateEndOfLeaseDialogInfo {
  endOfLeaseDate?: Date;
  comment?: string;
}

export class UpdateEndOfLease {
  residentId!: string;
  applicationId!: string;
  leaseStartDate?: Date;
  leaseEndDate?: Date;
  comment?: string;
}

export class ReturnSecurityDeposit {
  residentId!: string;
  applicationId!: string;
}

export interface TabItem {
  label: string;
  icon: string;
  route: string;
}

export class GetFullInsightReportViewModel {
  isOptIn?: boolean | null;
}
